import { useEffect, useState } from "react";
import { DeviceEnum } from "../../types/context-detection";
import { SearchboxViewType } from "../../types/searchbox";
import { subscribeToResize } from "../../util/resizeObserver";
import { IArticleSuggestion, IFlexibleSetSuggestion } from "../../types/suggestion-response";

type SuggestionsRenderType = "column" | "list";

/**
 * Custom React hook that determines how many products to render in suggestions,
 * adapting to runtime factors like view type, device, render mode, DOM layout, and window size.
 *
 * Subscribes to resize events and updates the count when dependencies or viewport width change.
 *
 * @param view - Current searchbox view type.
 * @param deviceType - Device type (desktop, tablet, phone, etc.).
 * @param suggestionsRenderType - Suggestion render mode (column or list).
 * @param hasMultipleContainers - Whether suggestions are split across multiple containers.
 * @returns Number of products to render in the suggestions panel.
 */
export function useProductRenderAmount(
    view: SearchboxViewType,
    deviceType: DeviceEnum,
    suggestionsRenderType: SuggestionsRenderType,
    hasMultipleContainers: boolean
): number {
    const [productsToRender, setProductsToRender] = useState(
        getProductRenderAmount(view, deviceType, hasMultipleContainers, suggestionsRenderType)
    );

    useEffect(() => {
        // React to changes in dependencies
        setProductsToRender(
            getProductRenderAmount(view, deviceType, hasMultipleContainers, suggestionsRenderType)
        );

        // Subscribe to resize updates
        const unsubscribe = subscribeToResize((_width) => {
            setProductsToRender(
                getProductRenderAmount(
                    view,
                    deviceType,
                    hasMultipleContainers,
                    suggestionsRenderType
                )
            );
        });

        return unsubscribe;
    }, [view, deviceType, suggestionsRenderType, hasMultipleContainers]);

    return productsToRender;
}

function getProductRenderAmount(
    view: SearchboxViewType,
    deviceType: DeviceEnum,
    hasMultipleContainers: boolean,
    suggestionsRenderType: SuggestionsRenderType
): number {
    let productsToRender = 0;

    if (view === "wide") {
        productsToRender = getAmountForWide(deviceType, hasMultipleContainers);
    } else if (view === "flyout") {
        productsToRender = getAmountForFlyout(deviceType);
    } else if (view === "grid" || view === "megamenu") {
        productsToRender = getAmountForGridAndMegaMenu(
            suggestionsRenderType,
            hasMultipleContainers
        );
    }

    return productsToRender;
}

function getAmountForWide(deviceType: DeviceEnum, hasMultipleContainers: boolean): number {
    const maxWidthForTwoColumns = 1349; // should have same value as $max-width-for-two-columns
    const maxPossibleProductAmount = 7;
    const currentWidth = window.innerWidth;

    if (deviceType === DeviceEnum.Tablet) {
        return 4;
    } else if (hasMultipleContainers && currentWidth <= maxWidthForTwoColumns) {
        return 4;
    } else if (!hasMultipleContainers || currentWidth > maxWidthForTwoColumns) {
        return maxPossibleProductAmount;
    }
    return maxPossibleProductAmount;
}

function getAmountForFlyout(deviceType: DeviceEnum): number {
    const maxWidthForOneColumn = 767;
    const currentWidth = window.innerWidth;

    if (deviceType === DeviceEnum.Desktop) {
        return currentWidth <= maxWidthForOneColumn ? 5 : 4;
    } else return 5;
}

function getAmountForGridAndMegaMenu(
    suggestionsRenderType: SuggestionsRenderType,
    hasMultipleContainers: boolean
): number {
    return suggestionsRenderType === "column" && !hasMultipleContainers ? 7 : 5;
}

/**
 * Crops article and set lists in-place to visibleProducts amount.
 */
export function cropProductLists(
    articles: IArticleSuggestion[],
    sets: IFlexibleSetSuggestion[],
    visibleProducts: number
) {
    const totalLength = articles.length + sets.length;
    const productsToCrop = Math.max(0, totalLength - visibleProducts);

    if (productsToCrop === 0) {
        return { articles, sets };
    }

    // crop articles list first and remaining from sets list
    const removeFromArticles = Math.min(productsToCrop, articles.length);
    if (removeFromArticles > 0) {
        articles.splice(articles.length - removeFromArticles, removeFromArticles);
    }

    const remainingToCrop = productsToCrop - removeFromArticles;
    const removeFromSets = Math.min(remainingToCrop, sets.length);
    if (removeFromSets > 0) {
        sets.splice(sets.length - removeFromSets, removeFromSets);
    }

    return { articles, sets };
}
